import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import NumericInput from '@/components/NumericInput';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Calculator, DollarSign, PieChart, Users } from 'lucide-react';

const CommissionCalculator = () => {
    const [values, setValues] = useState({
        price: '',
        commissionHtml: '5',
        legalExpenses: '0',
        splitAgent: '50', // Porcentaje del agente sobre la comisión
        splitAgency: '50',
        isSharedDeal: false, // Nuevo: Negocio compartido
        externalAgentSplit: '50' // Nuevo: Porcentaje para el otro agente (del total de comisión)
    });

    const [results, setResults] = useState(null);

    const calculate = () => {
        const price = parseFloat(values.price) || 0;
        const commRate = parseFloat(values.commissionHtml) || 0;
        const expenses = parseFloat(values.legalExpenses) || 0;
        const mySplitRate = parseFloat(values.splitAgent) || 0;

        // 1. Gross Commission
        const grossCommission = (price * commRate) / 100;

        // 2. Net Commission (after expenses)
        let netCommissionBase = grossCommission - expenses;
        if (netCommissionBase < 0) netCommissionBase = 0;

        let finalNetForUs = netCommissionBase;
        let externalAgentEarn = 0;

        // 3. Handle Shared Deal
        if (values.isSharedDeal) {
            const externalRate = parseFloat(values.externalAgentSplit) || 0;
            externalAgentEarn = (netCommissionBase * externalRate) / 100;
            finalNetForUs = netCommissionBase - externalAgentEarn;
        }

        // 4. Internal Split (Agent vs Agency)
        const myEarn = (finalNetForUs * mySplitRate) / 100;
        const agencyEarn = finalNetForUs - myEarn;

        const clientReceives = price - grossCommission; // Cliente paga comisión completa generalmente

        setResults({
            gross: grossCommission,
            netBase: netCommissionBase,
            externalAgent: externalAgentEarn,
            internalNet: finalNetForUs,
            myEarn: myEarn,
            agencyEarn: agencyEarn,
            client: clientReceives
        });
    };

    useEffect(() => {
        calculate();
    }, [values]);

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
    };

    return (
        <Card className="w-full max-w-4xl mx-auto shadow-lg">
            <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b">
                <CardTitle className="flex items-center gap-2 text-emerald-800">
                    <Calculator className="w-6 h-6" />
                    Calculadora de Comisiones
                </CardTitle>
                <CardDescription>Estima ganancias, splits con colegas y desglose interno.</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
                <div className="grid md:grid-cols-2 gap-8">
                    {/* INPUTS */}
                    <div className="space-y-6">
                        <div className="p-4 bg-gray-50 rounded-lg space-y-4 border border-gray-100">
                            <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                                <DollarSign className="w-4 h-4" /> Datos de la Propiedad
                            </h3>
                            <div className="space-y-2">
                                <Label>Precio de Venta ($)</Label>
                                <NumericInput
                                    placeholder="Ej: 250000"
                                    value={values.price}
                                    onChange={(val) => setValues({ ...values, price: val })}
                                    className="bg-white"
                                    prefix="$"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Comisión Total (%)</Label>
                                    <NumericInput
                                        value={values.commissionHtml}
                                        onChange={(val) => setValues({ ...values, commissionHtml: val })}
                                        className="bg-white"
                                        suffix="%"
                                        allowDecimals
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Gastos Legales ($)</Label>
                                    <NumericInput
                                        value={values.legalExpenses}
                                        onChange={(val) => setValues({ ...values, legalExpenses: val })}
                                        className="bg-white"
                                        prefix="$"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Shared Deal Toggle */}
                        <div className="p-4 bg-blue-50 rounded-lg space-y-4 border border-blue-100">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="shared-deal" className="flex items-center gap-2 cursor-pointer font-semibold text-blue-800">
                                    <Users className="w-4 h-4" /> ¿Negocio con otro Agente?
                                </Label>
                                <Switch
                                    id="shared-deal"
                                    checked={values.isSharedDeal}
                                    onCheckedChange={(c) => setValues({ ...values, isSharedDeal: c })}
                                />
                            </div>

                            {values.isSharedDeal && (
                                <div className="animate-in fade-in slide-in-from-top-2">
                                    <Label className="text-xs text-blue-600 mb-1 block">Porcentaje para Agente Externo (del Neto)</Label>
                                    <div className="flex items-center gap-2">
                                        <NumericInput
                                            value={values.externalAgentSplit}
                                            onChange={(val) => setValues({ ...values, externalAgentSplit: val })}
                                            className="bg-white"
                                            suffix="%"
                                            allowDecimals
                                        />
                                        <span className="font-bold text-gray-500">%</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Internal Split */}
                        <div className="space-y-2 pt-2">
                            <Label className="flex items-center gap-2"><Users className="w-4 h-4" /> Split Interno (Tú / Agencia)</Label>
                            <div className="flex items-center gap-4">
                                <div className="flex-1">
                                    <span className="text-xs text-gray-500 block mb-1">Tu Parte (%)</span>
                                    <NumericInput
                                        value={values.splitAgent}
                                        onChange={(val) => setValues({ ...values, splitAgent: val, splitAgency: 100 - (parseFloat(val) || 0) })}
                                        suffix="%"
                                        allowDecimals
                                    />
                                </div>
                                <div className="flex-1">
                                    <span className="text-xs text-gray-500 block mb-1">Agencia (%)</span>
                                    <NumericInput
                                        value={values.splitAgency}
                                        disabled
                                        className="bg-gray-100"
                                        suffix="%"
                                        allowDecimals
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RESULTS */}
                    <div className="bg-slate-50 rounded-xl p-6 space-y-6 flex flex-col justify-center">
                        <div className="flex items-center gap-2 mb-2">
                            <PieChart className="w-5 h-5 text-emerald-600" />
                            <h3 className="font-semibold text-gray-900">Resultado Financiero</h3>
                        </div>

                        {results && (
                            <div className="space-y-4">
                                {/* Top Hero Result */}
                                <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-emerald-600 to-green-500 rounded-xl shadow-lg text-white">
                                    <span className="text-sm font-medium opacity-90 mb-1">Tu Ganancia Neta</span>
                                    <span className="text-4xl font-bold">{formatCurrency(results.myEarn)}</span>
                                </div>

                                {/* Breakdown Grid */}
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    {values.isSharedDeal && (
                                        <div className="col-span-2 p-3 bg-blue-100 text-blue-800 rounded-lg flex justify-between items-center border border-blue-200">
                                            <span className="font-medium flex items-center gap-1"><Users className="w-3 h-3" /> Agente Externo</span>
                                            <span className="font-bold">{formatCurrency(results.externalAgent)}</span>
                                        </div>
                                    )}

                                    <div className="p-3 bg-white rounded-lg border">
                                        <span className="text-xs text-gray-500 block">Para Agencia</span>
                                        <span className="font-semibold text-gray-700">{formatCurrency(results.agencyEarn)}</span>
                                    </div>
                                    <div className="p-3 bg-white rounded-lg border">
                                        <span className="text-xs text-gray-500 block">Comisión Bruta</span>
                                        <span className="font-semibold text-gray-700">{formatCurrency(results.gross)}</span>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-gray-200 mt-2">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-gray-500">Gastos Legales</span>
                                        <span className="font-mono text-gray-900">-{formatCurrency(values.legalExpenses || 0)}</span>
                                    </div>
                                    <div className="flex justify-between items-center mt-2">
                                        <span className="text-sm font-semibold text-gray-700">Cliente Recibe (Aprox.)</span>
                                        <span className="font-mono font-bold text-gray-900 bg-gray-200 px-2 py-1 rounded">{formatCurrency(results.client)}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default CommissionCalculator;
